import * as React from 'react';
import {
    EmptyBox,
    HeaderListSkeleton,
    LazyBreadcrumb,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import {
    CollectionOpenSearch,
    CollectionOpenSearchSelectorMain,
    ORDERS_OPEN_SEARCH_SLICE_NAME,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import {
    OrderActionContext,
    ordersBasePath,
    OrderTable,
    PossibleTypes,
    useSearchBar,
} from '@europrocurement/l2d-modules';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { AnyAction } from '@reduxjs/toolkit';
import { Box } from '@mui/material';
import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useOrderTable } from '../../hooks';

const ListeOrders: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const datasource: DataSource<CollectionOpenSearch> = useSelector(
        CollectionOpenSearchSelectorMain,
    );
    const dispatch = useDispatch();
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { columns, filters, preFilters, rowActions } = useOrderTable();
    const [filtersInitialized, setFiltersInitialized] = React.useState<boolean>(false);
    const xIdSociete = useSociete();
    const { getOpenSearchCollections } = useFormaliteService();
    const { navigateOnClick } = useClickActions({ navigate });
    const { updatingOrder } = React.useContext(OrderActionContext);

    const breadcrumbItems = [
        {
            link: '/accueil',
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: { display: 'flex', alignItems: 'center' },
            },
        },
        {
            link: '/collections',
            path: 'Formalités',
        },
        'Liste des commandes',
    ];

    // Activation du SearchBar pour les groupes de travail
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityOrder);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        dispatch({
            type: `${ORDERS_OPEN_SEARCH_SLICE_NAME}/setmainPagination`,
            payload: {
                page: 1,
                itemsPerPage: 10,
            },
        });
        dispatch({
            type: `${ORDERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'societeId',
                value: [xIdSociete],
            },
        });
        dispatch(getOpenSearchCollections({}) as unknown as AnyAction);
        setFiltersInitialized(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [xIdSociete, updatingOrder]);

    const handleRowClick = React.useCallback(
        ({ id }: CollectionOpenSearch) => {
            navigateOnClick({ path: `/${ordersBasePath}/${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        ({ id }: CollectionOpenSearch) => {
            navigateOnClick({ path: `/${ordersBasePath}/${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    if (!filtersInitialized) {
        return null;
    }
    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <LazyBreadcrumb
                path={breadcrumbItems}
                separator=" / "
                sx={{ marginBottom: '1.5em' }}
            />
            <OrderTable
                datasource={datasource}
                columns={columns}
                filters={filters}
                preFilters={preFilters}
                rowActions={rowActions}
                onRowClick={handleRowClick}
                onWheelClick={handleRowWheelClick}
            />
        </SkeletonLoader>
    );
};

export default ListeOrders;
